import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import { MdClose } from 'react-icons/md';

const sizeClasses = {
  default: 'max-w-lg',
  video: 'max-w-screen-lg w-full',
};

const Modal = ({ isOpen, onClose, children, title, className = "", size = "default" }) => {
  // Close modal on Escape key press
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape' && isOpen) {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
      // Prevent body scroll when modal is open
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }

    return () => {
      document.removeEventListener('keydown', handleEscape);
      document.body.style.overflow = 'unset';
    };
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  return (
    <div 
      className="fixed inset-0 bg-background/80 backdrop-blur-md flex items-center justify-center z-50 p-4 animate-fade-in"
      onClick={onClose}
      role="dialog"
      aria-modal="true"
      aria-labelledby={title ? "modal-title" : undefined}
    >
      {/* Modal Container */}
      <div 
        className={`bg-surface border border-border rounded-2xl shadow-2xl mx-auto relative overflow-hidden max-h-[90vh] 
                   transform transition-all duration-300 animate-fade-in-up
                   ${sizeClasses[size]} 
                   ${className}`}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Modal Header for non-video modals */}
        {size !== 'video' && title && (
          <div className="flex items-center justify-between p-6 border-b border-border">
            <h2 id="modal-title" className="text-2xl font-display font-bold text-text">
              {title}
            </h2>
            <button
              onClick={onClose}
              className="flex-shrink-0 flex items-center justify-center w-10 h-10 text-text-subtle hover:text-text hover:bg-white/10 rounded-full transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-primary"
              aria-label="Close modal"
            >
              <MdClose size={24} />
            </button>
          </div>
        )}
        
        {/* Close button for Video modal, positioned above content */}
        {size === 'video' && (
           <button
            onClick={onClose}
            className="absolute top-3 right-3 z-20 flex-shrink-0 flex items-center justify-center w-10 h-10 text-white bg-black/40 hover:bg-black/70 rounded-full transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-primary"
            aria-label="Close modal"
          >
            <MdClose size={24} />
          </button>
        )}

        {/* Modal Content Area */}
        <div className={`overflow-y-auto max-h-[calc(90vh-8rem)] ${size !== 'video' ? 'p-6' : 'p-0'}`}>
          {children}
        </div>
      </div>
    </div>
  );
};

Modal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  children: PropTypes.node.isRequired,
  title: PropTypes.string,
  className: PropTypes.string,
  size: PropTypes.oneOf(['default', 'video']),
};

export default Modal;
